import React, { useState, useRef, useEffect } from 'react'
import { MdCheck, MdKeyboardArrowDown } from 'react-icons/md'

export type TSelectOption = {
  readonly value: string
  readonly label: string
}

export type TSelect = {
  readonly caption?: string
  readonly placeholder?: string
  readonly options: TSelectOption[]
  readonly value: string
  readonly onChange: (value: string) => void
  readonly error?: boolean
  readonly errorText?: string
}

export default function Select({
  caption,
  placeholder,
  options,
  value,
  onChange,
  error,
  errorText,
}: TSelect) {
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleSelect = (optionValue: string) => {
    setIsOpen(false)
    onChange(optionValue)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className='relative flex w-full flex-col gap-2' ref={selectRef}>
      <span className='text-base font-semibold text-pop-gray-1'>{caption}</span>
      <div className='sentry-mask relative'>
        <div
          className={`w-full rounded-lg border ${
            error ? 'border-pop-error' : 'border-pop-gray-5'
          } cursor-pointer px-3 py-4`}
          onClick={handleToggle}
          tabIndex={0}
        >
          <div className='flex items-center justify-between'>
            <div className='text-sm font-light'>
              {value
                ? options.find((opt) => opt.value === value)?.label
                : placeholder ?? 'Choose Answer'}
            </div>
            <MdKeyboardArrowDown className='h-6 w-6 text-pop-grey-600' />
          </div>
        </div>
        {isOpen && (
          <div className='absolute z-10 mt-1 max-h-96 w-full overflow-y-auto rounded-md bg-white shadow-lg'>
            {options.map((option) => (
              <div
                key={option.value}
                className='flex cursor-pointer flex-row items-center justify-between px-4 py-2 text-sm hover:bg-gray-200'
                onClick={() => handleSelect(option.value)}
              >
                <span className={`${value === option.value ? 'font-bold' : 'font-normal'}`}>
                  {option.label}
                </span>
                {value === option.value && <MdCheck className='h-6 w-6 text-pop-blue-600' />}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <span className='text-xs text-pop-error'>{errorText}</span>}
    </div>
  )
}
